import type { DashboardGetStudentExitSlipQuery$data } from '../../graphQLOperations/__generated__/DashboardGetStudentExitSlipQuery.graphql';
import type { ExitSlipResult, TodaysExitSlip } from '../../models/ExitSlip';
import type { SkillResourceStepSlug } from '../../models/Timeline';

// What the collapsed banner shows once today's exit ticket is finished. The four states come from the
// design; which one applies is resolved by resolveExitSlipOutcome below.
export type ExitSlipOutcome = 'taken' | 'passed' | 'on-fire' | 'passed-and-demonstrated';

// Where the collapsed banner's call to action sends the student, per outcome. Below the bar — or barely
// over it — more practice is what helps, so they land on Practice Problems. The two strong outcomes
// have already shown the skill, so they land on the Skill Challenge instead. The banner's copy for each
// outcome (see DashboardExitSlipCollapsedBanner's outcomeCopy) is worded to match.
export const exitSlipCtaStep: Record<ExitSlipOutcome, SkillResourceStepSlug | null> = {
  taken: 'practice-problems',
  passed: 'practice-problems',
  'on-fire': 'skill-challenge',
  'passed-and-demonstrated': null,
};

// Two thirds of the points available: what makes the collapsed banner read "Passed", the bar the
// unit-path cards' own exit-ticket badges are scored against, and what a ticket needs to count towards
// the unit header's exit-slip badge.
export const EXIT_SLIP_PASSING_RATIO = 2 / 3;

// Points earned over points available — not the all-or-nothing per-question count the result modal
// uses, because the design shows percentages (39%, 80%) that only a raw score ratio can produce.
export const exitSlipScoreRatio = (result: ExitSlipResult[]): number => {
  const maxScore = result.reduce((total, item) => total + item.maxScore, 0);
  return maxScore === 0 ? 0 : result.reduce((total, item) => total + item.score, 0) / maxScore;
};

/**
 * Whether a graded result clears the passing bar.
 *
 * Scored on points, which is not the same question as "how many questions did they get right": an
 * item can be worth partial credit, so a student can clear two thirds of the points while having got
 * fewer than two thirds of the items fully right. The points reading is the one the rest of the
 * feature uses — the badge, the banner, the unit-path cards — so anything deciding what the student
 * has earned has to go through here rather than counting items.
 */
export const isPassingExitSlipResult = (result: ExitSlipResult[]): boolean =>
  exitSlipScoreRatio(result) >= EXIT_SLIP_PASSING_RATIO;

export interface ExitSlipOutcomeInputs {
  scoreRatio: number;
  // TODO: the "fire label" rule that turns a passed exit ticket into a skill-challenge recommendation
  // is not defined yet, so nothing sets this and 'on-fire' is currently unreachable. Pass it once the
  // rule exists — no other code needs to change.
  isOnFire?: boolean;
  // TODO: whether passing this exit ticket also completed the lesson's skill ("+1 skill done"). Same
  // as above: unset for now.
  hasDemonstratedSkill?: boolean;
}

// Below the passing bar the student has only "Taken" the ticket. At or above it, demonstrating the
// skill outranks the fire recommendation, which outranks a plain pass.
export const resolveExitSlipOutcome = ({
  scoreRatio,
  isOnFire,
  hasDemonstratedSkill,
}: ExitSlipOutcomeInputs): ExitSlipOutcome => {
  if (scoreRatio < EXIT_SLIP_PASSING_RATIO) {
    return 'taken';
  }
  if (hasDemonstratedSkill) {
    return 'passed-and-demonstrated';
  }
  return isOnFire ? 'on-fire' : 'passed';
};

type ClassExitSlips = DashboardGetStudentExitSlipQuery$data['classExitSlips'];
type ClassExitSlip = ClassExitSlips[number];

// A ticket can carry more than one session (a retake, or an abandoned attempt followed by a real one),
// so the finished one has to be picked out rather than taken from the front of the list. An unfinished
// session has no meaningful score — its `score` is 0 and, unlike under the old day-scoped session
// filter, it does now come back with the query.
const findCompletedSession = (slip: ClassExitSlip) =>
  slip.learnosityExitSlipSessions?.find(session => session?.isComplete) ?? null;

// The single skill an exit ticket is about. A lesson can list several, but the ticket's Learnosity
// activity is built from the first one alone (see DashboardExitSlipModal's activityId), so that is the
// only skill the ticket covers.
const exitSlipSkill = (slip: ClassExitSlip) => slip.curriculumLesson?.skills?.[0] ?? null;

// The unit (target) a ticket's lesson belongs to. Null is possible per the schema and, being the only
// thing that files a ticket under a unit, leaves the ticket out of the header badge entirely.
const exitSlipTargetId = (slip: ClassExitSlip) => slip.curriculumLesson?.target?.id ?? null;

export interface StudentExitSlipsSummary {
  // Every exit ticket the student has finished, over every day queried and across every unit —
  // whatever they scored on it. Ids rather than a bare length because this doubles as the signal that
  // the server has caught up with a finish shown optimistically (see the pending finish stored below);
  // that reading is why the list has to stay whole-history and cross-unit, unlike the badge's.
  doneExitSlipIds: string[];
  // The tickets the student passed, filed under the unit (target) whose badge they count towards. The
  // header's badge reports the unit on screen alone, so it reads one bucket rather than the list above.
  // It is about tickets rather than skills, so two tickets for the same skill count twice and nothing
  // needs deduping. Tickets with no target are dropped — see exitSlipTargetId.
  passedExitSlipIdsByTarget: Record<string, string[]>;
  // Skills covered by every ticket the student has finished, passed or not. Sitting the ticket is what
  // opens that skill's card on the unit path, so the score does not come into it.
  completedTicketSkillIds: string[];
  // The ticket due today, or null when the student has none. Only this one is ever shown on screen.
  todaysExitSlip: TodaysExitSlip | null;
}

const toTodaysExitSlip = (slip: ClassExitSlip): TodaysExitSlip => {
  const completedSession = findCompletedSession(slip);
  const lessonSkill = exitSlipSkill(slip);
  return {
    id: slip.id,
    isComplete: !!completedSession,
    // A finished session's { score, maxScore } is the same shape the modal hands back when the student
    // finishes, so one ratio helper serves both sources.
    scoreRatio: completedSession ? exitSlipScoreRatio([completedSession]) : null,
    targetId: exitSlipTargetId(slip),
    curriculumLesson: {
      id: slip.curriculumLesson?.id ?? '',
      number: slip.curriculumLesson?.number ?? '',
      name: slip.curriculumLesson?.name ?? '',
      hasExitSlip: slip.curriculumLesson?.hasExitSlip ?? false,
      skill: lessonSkill
        ? { id: lessonSkill.id ?? '', code: lessonSkill.code ?? '', name: lessonSkill.name ?? '' }
        : null,
    },
  };
};

/**
 * Reduces the student's whole exit-ticket history (see the getStudentExitSlip loader in
 * DashboardStudent, which asks for every ticket due up to today) to what the dashboard needs: the
 * tickets they have finished, the ones they passed filed by unit for the header badge, and today's
 * ticket.
 *
 * `todayKey` is 'yyyy-MM-dd' in the student's own timezone.
 */
export const summarizeStudentExitSlips = (
  classExitSlips: ClassExitSlips,
  todayKey: string,
): StudentExitSlipsSummary => {
  // `dueOn` is a DateOnly server-side, so it arrives as a bare 'yyyy-MM-dd' with no time and no zone —
  // compared as a string against `todayKey`, which is the same day the server's dueOnTo filter was
  // given. Parsing either side into a Date would only introduce a timezone that neither value has, and
  // could land a ticket on the day either side of the one it is assigned to. The slice is belt and
  // braces in case the scalar ever grows a time component.
  const todaysSlip = classExitSlips.find(slip => String(slip.dueOn).slice(0, 10) === todayKey);

  // Sitting the ticket is enough to unlock its card; only a pass counts towards the header badge.
  const doneSlips = classExitSlips.filter(slip => !!findCompletedSession(slip));
  const passedSlips = classExitSlips.filter(slip => {
    const completedSession = findCompletedSession(slip);
    return !!completedSession && isPassingExitSlipResult([completedSession]);
  });

  const passedExitSlipIdsByTarget: Record<string, string[]> = {};
  passedSlips.forEach(slip => {
    const targetId = exitSlipTargetId(slip);
    if (!targetId) {
      return;
    }
    passedExitSlipIdsByTarget[targetId] = [...(passedExitSlipIdsByTarget[targetId] ?? []), slip.id];
  });

  return {
    doneExitSlipIds: doneSlips.map(slip => slip.id),
    passedExitSlipIdsByTarget,
    completedTicketSkillIds: doneSlips.flatMap(slip => {
      const skillId = exitSlipSkill(slip)?.id;
      return skillId ? [skillId] : [];
    }),
    todaysExitSlip: todaysSlip ? toTodaysExitSlip(todaysSlip) : null,
  };
};

/**
 * Both records below live in localStorage, which belongs to the DEVICE, while what they describe
 * belongs to one student. The student therefore has to be part of the key.
 *
 * The ticket id cannot stand in for them. A `class_exit_slips` row is one per class, so every student
 * in the class is offered the identical `ClassExitSlipNode` id — on a shared classroom Chromebook the
 * first student to close their result card would otherwise suppress the invitation for everyone who
 * used the device next, and a stored finish would show them a classmate's score.
 *
 * Mirrors the `pp-progress:<user>:<skill>` convention in practiceProblemsHelper.
 */
const dismissedStorageKey = (userId: string) => `exit_slip_dismissed_id:${userId}`;
const pendingFinishStorageKey = (userId: string) => `exit_slip_pending_finish:${userId}`;

// The id of the exit ticket whose result card the student closed. Only the dismissal needs persisting —
// the score itself comes back with the query — and keying it by ticket id gives the "must not outlive
// the day" rule for free: tomorrow's ticket has a different id, so the card returns on its own.
export const readDismissedExitSlipId = (userId: string | undefined): string | null => {
  // No student, no record to find. Reading an unscoped key instead would be the very leak this
  // scoping exists to prevent, so absence reads as "nothing dismissed".
  if (!userId) {
    return null;
  }
  try {
    return localStorage.getItem(dismissedStorageKey(userId));
  } catch {
    return null;
  }
};

export const writeDismissedExitSlipId = (userId: string | undefined, exitSlipId: string): void => {
  if (!userId) {
    return;
  }
  try {
    localStorage.setItem(dismissedStorageKey(userId), exitSlipId);
  } catch {
    // A full or unavailable localStorage only costs the dismissal its persistence across reloads, so
    // the caller carries on with the in-memory state.
  }
};

/**
 * The finish this device has seen but the server has not reported back yet: which ticket, and how the
 * student scored on it.
 *
 * Two things need it. The collapsed banner has to keep showing the result once the student navigates
 * off the dashboard and back — the freshly graded result is in-memory only, so without this there is
 * no score left to show until the query catches up, and the banner would vanish. And the exit-slip
 * badge counts the finish on top of the query's tally, so the number moves the moment they finish
 * rather than waiting for a reload.
 *
 * Storing the ticket *id* — not an incremented count — is what makes the badge side safe: on the next
 * load the query either already reports a completed session for that ticket, in which case the record
 * is dropped and the server's number stands alone, or it does not, in which case the record is still
 * needed and survives the reload. A stored count could not tell those apart and would double-count.
 */
export interface PendingExitSlipFinish {
  exitSlipId: string;
  scoreRatio: number;
  // The unit (target) whose badge this finish counts towards — the ticket's own unit, which is not
  // always the one on screen. Optional only to survive records written before the badge became
  // unit-scoped: such a record still shows its result card, but counts towards no badge rather than
  // guessing a unit. The window is one day, after which the query reports the finish itself.
  targetId?: string;
}

/**
 * Whether the stored record is the one covering this ticket.
 *
 * Both sides are routinely absent — no ticket due today, no finish stored — and two absences are not
 * a match: comparing `pendingFinish?.exitSlipId === todaysExitSlip?.id` directly would read
 * `undefined === undefined` as a finish that never happened, and hide an invitation the student is
 * owed.
 */
export const isPendingFinishFor = (
  pendingFinish: PendingExitSlipFinish | null,
  exitSlipId: string | undefined,
): boolean => !!pendingFinish && !!exitSlipId && pendingFinish.exitSlipId === exitSlipId;

export const readPendingExitSlipFinish = (userId: string | undefined): PendingExitSlipFinish | null => {
  if (!userId) {
    return null;
  }
  try {
    const raw = localStorage.getItem(pendingFinishStorageKey(userId));
    if (!raw) {
      return null;
    }
    // Shape-checked rather than trusted: the key outlives deploys, so an entry written by an older
    // version has to read as "nothing pending" instead of rendering a banner with undefined in it.
    const stored = JSON.parse(raw) as PendingExitSlipFinish;
    if (typeof stored?.exitSlipId !== 'string' || typeof stored?.scoreRatio !== 'number') {
      return null;
    }
    // `targetId` is the one field a pre-unit-scoping record can be missing, and the record is still
    // worth having without it — it is rebuilt here rather than passed through so anything that is not
    // a string reaches the caller as absent, not as a key that will never match a unit.
    return {
      exitSlipId: stored.exitSlipId,
      scoreRatio: stored.scoreRatio,
      targetId: typeof stored.targetId === 'string' ? stored.targetId : undefined,
    };
  } catch {
    return null;
  }
};

export const writePendingExitSlipFinish = (userId: string | undefined, finish: PendingExitSlipFinish): void => {
  if (!userId) {
    return;
  }
  try {
    localStorage.setItem(pendingFinishStorageKey(userId), JSON.stringify(finish));
  } catch {
    // Same as the dismissal above: without storage the record simply does not survive a reload, and by
    // then the query has usually caught up anyway.
  }
};

export const clearPendingExitSlipFinish = (userId: string | undefined): void => {
  if (!userId) {
    return;
  }
  try {
    localStorage.removeItem(pendingFinishStorageKey(userId));
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
};
